import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api, { getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ThemeContext = createContext(null);
export const useTheme = () => useContext(ThemeContext);

const KEY = "hunter_theme";
const media = () => window.matchMedia("(prefers-color-scheme: dark)");

function applyTheme(theme) {
  const dark = theme === "dark" || (theme === "system" && media().matches);
  document.documentElement.classList.toggle("dark", dark);
  document.documentElement.style.colorScheme = dark ? "dark" : "light";
  return dark ? "dark" : "light";
}

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const [theme, setThemeState] = useState(() => localStorage.getItem(KEY) || "system");
  const [resolved, setResolved] = useState(() => applyTheme(localStorage.getItem(KEY) || "system"));
  const explicit = useRef(!!localStorage.getItem(KEY));

  useEffect(() => { setResolved(applyTheme(theme)); }, [theme]);

  // React to OS changes while in system mode
  useEffect(() => {
    const m = media();
    const handler = () => { if (theme === "system") setResolved(applyTheme("system")); };
    m.addEventListener("change", handler);
    return () => m.removeEventListener("change", handler);
  }, [theme]);

  // Adopt account/default theme when the user has not set an explicit preference on this device
  useEffect(() => {
    if (!user || !user.id || explicit.current) return;
    (async () => {
      if (user.theme && ["light", "dark", "system"].includes(user.theme)) {
        setThemeState(user.theme);
        return;
      }
      try {
        const { data } = await api.get("/settings/app-config");
        if (data?.default_theme) setThemeState(data.default_theme);
      } catch (e) { console.error("Theme config load failed", e); }
    })();
  }, [user]);

  const setTheme = useCallback((t) => {
    explicit.current = true;
    localStorage.setItem(KEY, t);
    setThemeState(t);
    if (getToken()) api.post("/auth/preferences", { theme: t }).catch(() => {});
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, resolved, setTheme }}>{children}</ThemeContext.Provider>
  );
}
